import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  CloudUpload,
  Download,
  Refresh,
  Close as CloseIcon,
  Link as LinkIcon,
  ContentCopy,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { useState, useEffect, useRef } from "react";
import eventService from "../../services/event";
import seatingService from "../../services/seating";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

interface Event {
  id: number;
  name: string;
}

interface Category {
  id: number;
  name: string;
  price: number;
  stock: number;
}

interface Ticket {
  id: number;
  code: string;
  seat: string | null;
  status: string;
  category_id: number;
  category_name: string;
  category_price: number;
  holder_name: string | null;
  holder_email: string | null;
  holder_phone: string | null;
}

interface JobStatus {
  id: number;
  filename: string;
  total_rows: number;
  processed_rows: number;
  success_count: number;
  error_count: number;
  status: string;
  error_details: any[];
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  progress: number;
}

export default function TicketManagement() {
  const [selectedEvent, setSelectedEvent] = useState<number | "">("");
  const [selectedCategory, setSelectedCategory] = useState<number | "">("");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [generatedLink, setGeneratedLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jobStatusInterval = useRef<NodeJS.Timeout | null>(null);

  // Fetch events
  const { data: eventsData } = useQuery({
    queryKey: ["events"],
    queryFn: () => eventService.getAllEvent({ keyword: "" }),
  });

  // Fetch categories when event is selected
  const { data: categoriesData, refetch: refetchCategories } = useQuery({
    queryKey: ["categories", selectedEvent],
    queryFn: () => seatingService.getCategoriesByEvent(selectedEvent as number),
    enabled: !!selectedEvent,
  });

  // Fetch tickets
  const {
    data: ticketsData,
    refetch: refetchTickets,
    isLoading: loadingTickets,
  } = useQuery({
    queryKey: ["tickets", selectedEvent, selectedCategory, keyword, page, rowsPerPage],
    queryFn: () =>
      seatingService.getTicketsByEvent({
        event_id: selectedEvent as number,
        category_id: selectedCategory ? (selectedCategory as number) : undefined,
        keyword,
        page: page + 1,
        pageSize: rowsPerPage,
      }),
    enabled: !!selectedEvent,
  });

  // Fetch job status
  const { data: jobStatusData, refetch: refetchJobStatus } = useQuery({
    queryKey: ["job-status", selectedEvent, selectedCategory],
    queryFn: () =>
      seatingService.getJobStatus(
        selectedEvent as number,
        selectedCategory ? (selectedCategory as number) : undefined
      ),
    enabled: !!selectedEvent,
    refetchInterval: (data: any) => {
      // Only auto-refetch if job is pending or processing
      if (data?.data?.status === "pending" || data?.data?.status === "processing") {
        return 2000; // Poll every 2 seconds
      }
      return false; // Stop polling
    },
  });

  const events: Event[] = eventsData?.data || [];
  const categories: Category[] = categoriesData?.data || [];
  const tickets: Ticket[] = ticketsData?.data || [];
  const total = ticketsData?.total || 0;
  const jobStatus: JobStatus | null = jobStatusData?.data || null;

  // Reset filters when event changes
  useEffect(() => {
    setSelectedCategory("");
    setKeyword("");
    setPage(0);
  }, [selectedEvent]);

  // Handle event change
  const handleEventChange = (eventId: number | "") => {
    setSelectedEvent(eventId);
  };

  // Handle download template
  const handleDownloadTemplate = async () => {
    if (!selectedEvent) return;

    try {
      const eventName = events.find((e) => e.id === selectedEvent)?.name;
      await seatingService.downloadTemplate(
        selectedEvent as number,
        selectedCategory ? (selectedCategory as number) : undefined,
        eventName
      );
    } catch (error: any) {
      console.error("Error downloading template:", error);
      setUploadError("Failed to download template");
    }
  };

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadError("");
      setUploadSuccess("");
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadFile || !selectedEvent) return;

    setUploading(true);
    setUploadError("");
    setUploadSuccess("");

    try {
      await seatingService.uploadSeatingFile(
        uploadFile,
        selectedEvent as number,
        selectedCategory ? (selectedCategory as number) : undefined
      );

      setUploadSuccess("File uploaded successfully! Processing in background...");
      setUploadFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Start polling for job status
      setTimeout(() => {
        refetchJobStatus();
      }, 1000);
    } catch (error: any) {
      console.error("Error uploading file:", error);
      setUploadError(
        error.response?.data?.message || "Failed to upload file"
      );
    } finally {
      setUploading(false);
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "processing":
        return "info";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle refresh
  const handleRefresh = () => {
    refetchTickets();
    refetchJobStatus();
  };

  // Handle generate link
  const handleGenerateLink = async (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowLinkDialog(true);
    setGeneratingLink(true);
    setLinkError("");
    setGeneratedLink("");
    setCopySuccess(false);

    try {
      const response = await seatingService.generateTicketLink(ticket.id);
      setGeneratedLink(response.data.link);
    } catch (error: any) {
      console.error("Error generating link:", error);
      setLinkError(
        error.response?.data?.message || "Failed to generate ticket link"
      );
    } finally {
      setGeneratingLink(false);
    }
  };

  // Handle copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
    }
  };

  // Handle close link dialog
  const handleCloseLinkDialog = () => {
    setShowLinkDialog(false);
    setSelectedTicket(null);
    setGeneratedLink("");
    setLinkError("");
    setCopySuccess(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Ticket Management
      </Typography>

      {/* Event Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Event</InputLabel>
                <Select
                  value={selectedEvent}
                  label="Select Event"
                  onChange={(e) => handleEventChange(e.target.value as number)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {events.map((event) => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {selectedEvent && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Filter by Category"
                    onChange={(e) => setSelectedCategory(e.target.value as number)}
                  >
                    <MenuItem value="">
                      <em>All Categories</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name} ({category.stock} tickets)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {selectedEvent && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upload Seating Assignments
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    fullWidth
                    onClick={handleDownloadTemplate}
                  >
                    Download Template
                  </Button>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUpload />}
                    fullWidth
                    disabled={uploading}
                  >
                    Choose File
                    <VisuallyHiddenInput
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                    />
                  </Button>
                  {uploadFile && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Selected: {uploadFile.name}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleUpload}
                    disabled={!uploadFile || uploading}
                  >
                    {uploading ? "Uploading..." : "Upload & Process"}
                  </Button>
                </Grid>
              </Grid>

              {uploadError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {uploadError}
                </Alert>
              )}

              {uploadSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {uploadSuccess}
                </Alert>
              )}

              {/* Job Status */}
              {jobStatus && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Upload Status
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Typography variant="body2">
                          <strong>File:</strong> {jobStatus.filename}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Status:</strong>{" "}
                          <Chip
                            label={jobStatus.status.toUpperCase()}
                            color={getStatusColor(jobStatus.status) as any}
                            size="small"
                          />
                        </Typography>
                        <Typography variant="body2">
                          <strong>Progress:</strong> {jobStatus.processed_rows} /{" "}
                          {jobStatus.total_rows} rows
                        </Typography>
                        <Typography variant="body2">
                          <strong>Success:</strong> {jobStatus.success_count} |{" "}
                          <strong>Errors:</strong> {jobStatus.error_count}
                        </Typography>

                        {(jobStatus.status === "processing" ||
                          jobStatus.status === "pending") && (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={jobStatus.progress}
                            />
                            <Typography variant="caption">
                              {jobStatus.progress.toFixed(0)}%
                            </Typography>
                          </Box>
                        )}
                      </Grid>

                      <Grid item xs={12} md={4}>
                        {jobStatus.error_count > 0 && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            fullWidth
                            onClick={() => setShowErrorDialog(true)}
                          >
                            View Errors ({jobStatus.error_count})
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Tickets Table */}
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Tickets</Typography>
                <IconButton onClick={handleRefresh} size="small">
                  <Refresh />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                label="Search by ticket code, seat, name, or email"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(0);
                }}
                sx={{ mb: 2 }}
              />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket ID</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Seat</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Holder Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingTickets ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : tickets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No tickets found
                        </TableCell>
                      </TableRow>
                    ) : (
                      tickets.map((ticket) => (
                        <TableRow key={ticket.id}>
                          <TableCell>{ticket.id}</TableCell>
                          <TableCell>{ticket.code}</TableCell>
                          <TableCell>
                            {ticket.seat ? (
                              <Chip label={ticket.seat} size="small" color="primary" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Not assigned
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{ticket.category_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={ticket.status}
                              size="small"
                              color={
                                ticket.status === "sold"
                                  ? "success"
                                  : ticket.status === "available"
                                  ? "default"
                                  : "warning"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {ticket.holder_name || (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {ticket.holder_email || (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleGenerateLink(ticket)}
                              disabled={ticket.status !== "sold"}
                              title={
                                ticket.status === "sold"
                                  ? "Generate ticket link"
                                  : "Ticket must be sold to generate link"
                              }
                            >
                              <LinkIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>

          {/* Error Dialog */}
          <Dialog
            open={showErrorDialog}
            onClose={() => setShowErrorDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Upload Errors
              <IconButton
                onClick={() => setShowErrorDialog(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {jobStatus?.error_details && jobStatus.error_details.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Ticket ID</TableCell>
                        <TableCell>Seat</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobStatus.error_details.map((error: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{error.row}</TableCell>
                          <TableCell>{error.ticket_id || "-"}</TableCell>
                          <TableCell>{error.seat || "-"}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="error">
                              {error.error}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography>No errors to display</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowErrorDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Generate Link Dialog */}
          <Dialog
            open={showLinkDialog}
            onClose={handleCloseLinkDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Generate Ticket Link
              <IconButton
                onClick={handleCloseLinkDialog}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {selectedTicket && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Ticket ID:</strong> {selectedTicket.id}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Code:</strong> {selectedTicket.code}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Holder:</strong> {selectedTicket.holder_name || "N/A"}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Email:</strong> {selectedTicket.holder_email || "N/A"}
                  </Typography>
                </Box>
              )}

              {generatingLink ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Generating ticket link...
                  </Typography>
                </Box>
              ) : linkError ? (
                <Alert severity="error">{linkError}</Alert>
              ) : generatedLink ? (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Ticket link generated successfully!
                  </Alert>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: "break-all",
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      {generatedLink}
                    </Typography>
                  </Paper>
                  <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyLink}
                      fullWidth
                    >
                      {copySuccess ? "Copied!" : "Copy Link"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleGenerateLink(selectedTicket!)}
                      fullWidth
                    >
                      Regenerate
                    </Button>
                  </Box>
                  {copySuccess && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Link copied to clipboard!
                    </Alert>
                  )}
                </>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseLinkDialog}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
